import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import {
  CorpacerosSyncStartedEvent,
  CorpacerosSyncProgressEvent,
  CorpacerosSyncCompletedEvent,
  CorpacerosSyncFailedEvent
} from '../modules/epps/models/corpaceros-sync.models';
import {
  CorpacerosUserSyncStartedEvent,
  CorpacerosUserSyncProgressEvent,
  CorpacerosUserSyncCompletedEvent,
  CorpacerosUserSyncFailedEvent
} from '../modules/epps/models/corpaceros-user-sync.models';
import { EppDeliverySyncEvent, EppDeliverySyncFailedEvent } from '../modules/administration/models/epp-integration.models';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection!: signalR.HubConnection;
  private authService: MsalService = inject(MsalService);

  // Subjects globales para eventos de Corpaceros (Fix del backend)
  private corpacerosSyncStarted$ = new Subject<CorpacerosSyncStartedEvent>();
  private corpacerosSyncProgress$ = new Subject<CorpacerosSyncProgressEvent>();
  private corpacerosSyncCompleted$ = new Subject<CorpacerosSyncCompletedEvent>();
  private corpacerosSyncFailed$ = new Subject<CorpacerosSyncFailedEvent>();
  private corpacerosSyncPing$ = new Subject<any>();

  // Subjects globales para eventos de sincronización de usuarios de Corpaceros
  private corpacerosUserSyncStarted$ = new Subject<CorpacerosUserSyncStartedEvent>();
  private corpacerosUserSyncProgress$ = new Subject<CorpacerosUserSyncProgressEvent>();
  private corpacerosUserSyncCompleted$ = new Subject<CorpacerosUserSyncCompletedEvent>();
  private corpacerosUserSyncFailed$ = new Subject<CorpacerosUserSyncFailedEvent>();

  // Subjects globales para eventos de Integraciones EPP
  private eppDeliverySync$ = new Subject<EppDeliverySyncEvent>();
  private eppDeliverySyncFailed$ = new Subject<EppDeliverySyncFailedEvent>();

  // Observables públicos para Corpaceros
  public corpacerosSyncStartedEvent$ = this.corpacerosSyncStarted$.asObservable();
  public corpacerosSyncProgressEvent$ = this.corpacerosSyncProgress$.asObservable();
  public corpacerosSyncCompletedEvent$ = this.corpacerosSyncCompleted$.asObservable();
  public corpacerosSyncFailedEvent$ = this.corpacerosSyncFailed$.asObservable();
  public corpacerosSyncPingEvent$ = this.corpacerosSyncPing$.asObservable();

  // Observables públicos para sincronización de usuarios de Corpaceros
  public corpacerosUserSyncStartedEvent$ = this.corpacerosUserSyncStarted$.asObservable();
  public corpacerosUserSyncProgressEvent$ = this.corpacerosUserSyncProgress$.asObservable();
  public corpacerosUserSyncCompletedEvent$ = this.corpacerosUserSyncCompleted$.asObservable();
  public corpacerosUserSyncFailedEvent$ = this.corpacerosUserSyncFailed$.asObservable();

  // Observables públicos para Integraciones EPP
  public eppDeliverySyncEvent$ = this.eppDeliverySync$.asObservable();
  public eppDeliverySyncFailedEvent$ = this.eppDeliverySyncFailed$.asObservable();
  
  constructor() { }

  /**
   * Expone la conexión para que otros servicios puedan registrar handlers
   */
  getConnection(): signalR.HubConnection {
    return this.hubConnection;
  }

  // Método para iniciar la conexión
  startConnection(endpoint: string): void {
    console.log('🔌 SignalR: Iniciando conexión a', endpoint);
    
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(endpoint, {
        accessTokenFactory: () => {
          console.log('🔑 SignalR: Obteniendo token de acceso...');
          const account = this.authService.instance.getActiveAccount();
          
          if (!account) {
            console.warn('⚠️ SignalR: No hay cuenta activa, conectando sin token');
            return Promise.resolve('');
          }
          
          let accessToken = this.authService.instance.acquireTokenSilent({
            scopes: environment.apiConfig.scopes,
            account: account
          }).then(response => {
            console.log('✅ SignalR: Token obtenido');
            return response.accessToken;
          }).catch(err => {
            console.error('❌ SignalR: Error obteniendo token:', err);
            return '';
          });
          
          return accessToken;
        }
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection
      .start()
      .then(() => {
        console.log('✅ SignalR: Conexión establecida');
        
        // Registrar handlers de Corpaceros después de conectar (Fix del backend)
        this.registerCorpacerosHandlers();
      })
      .catch(err => {
        console.error('❌ SignalR: Error al conectar:', err);
      });
  }

  // Método para escuchar eventos desde el servidor
  on(eventName: string, callback: (...args: any[]) => void): void {
    this.hubConnection.on(eventName, callback);
  }

  // Método para enviar datos al servidor
  send(eventName: string, data: any): void {
    this.hubConnection.invoke(eventName, data)
      .catch(err => console.error('Error while sending data: ', err));
  }

  // Método para detener la conexión
  stopConnection(): void {
    if (!this.hubConnection) {
      return;
    }
    this.hubConnection.stop()
      .then()
      .catch(err => console.error('Error while stopping SignalR connection: ', err));
  }

  /**
   * Registra handlers de Corpaceros en el servicio global (Fix del backend)
   * Esto permite que los eventos lleguen sin importar en qué página esté el usuario
   */
  private registerCorpacerosHandlers(): void {
    console.log('🌍 SignalR: Registrando handlers globales de Corpaceros...');
    
    this.hubConnection.on('corpacerosSyncStarted', (data) => {
      console.log('🟢 SignalR Global: corpacerosSyncStarted', data);
      this.corpacerosSyncStarted$.next(data);
    });
    
    this.hubConnection.on('corpacerosSyncProgress', (data) => {
      console.log('🔵 SignalR Global: corpacerosSyncProgress', data);
      this.corpacerosSyncProgress$.next(data);
    });
    
    this.hubConnection.on('corpacerosSyncCompleted', (data) => {
      console.log('✅ SignalR Global: corpacerosSyncCompleted', data);
      this.corpacerosSyncCompleted$.next(data);
    });
    
    this.hubConnection.on('corpacerosSyncFailed', (data) => {
      console.log('❌ SignalR Global: corpacerosSyncFailed', data);
      this.corpacerosSyncFailed$.next(data);
    });
    
    this.hubConnection.on('corpacerosSyncPing', (data) => {
      console.log('🏓 SignalR Global: corpacerosSyncPing', data);
      this.corpacerosSyncPing$.next(data);
    });
    
    console.log('✅ SignalR: Handlers globales de Corpaceros registrados');

    this.registerCorpacerosUserSyncHandlers();
    this.registerEppIntegrationHandlers();
  }

  private registerCorpacerosUserSyncHandlers(): void {
    console.log('🌍 SignalR: Registrando handlers globales de sincronización de usuarios Corpaceros...');

    this.hubConnection.on('corpacerosUserSyncStarted', (data) => {
      console.log('🟢 SignalR Global: corpacerosUserSyncStarted', data);
      this.corpacerosUserSyncStarted$.next(data);
    });

    this.hubConnection.on('corpacerosUserSyncProgress', (data) => {
      console.log('🔵 SignalR Global: corpacerosUserSyncProgress', data);
      this.corpacerosUserSyncProgress$.next(data);
    });

    this.hubConnection.on('corpacerosUserSyncCompleted', (data) => {
      console.log('✅ SignalR Global: corpacerosUserSyncCompleted', data);
      this.corpacerosUserSyncCompleted$.next(data);
    });

    this.hubConnection.on('corpacerosUserSyncFailed', (data) => {
      console.log('❌ SignalR Global: corpacerosUserSyncFailed', data);
      this.corpacerosUserSyncFailed$.next(data);
    });

    console.log('✅ SignalR: Handlers globales de sincronización de usuarios Corpaceros registrados');
  }

  private registerEppIntegrationHandlers(): void {
    console.log('🌍 SignalR: Registrando handlers globales de Integraciones EPP...');

    this.hubConnection.on('eppDeliverySync', (data: EppDeliverySyncEvent) => {
      console.log('🔗 SignalR Global: eppDeliverySync', data);
      this.eppDeliverySync$.next(data);
    });

    this.hubConnection.on('eppDeliverySyncFailed', (data: EppDeliverySyncFailedEvent) => {
      console.log('❌ SignalR Global: eppDeliverySyncFailed', data);
      this.eppDeliverySyncFailed$.next(data);
    });

    console.log('✅ SignalR: Handlers globales de Integraciones EPP registrados');
  }
}